import axios from "axios";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { VM_DOMAIN } from "../Util/URLConstants";
import { getSuperAdminId, withAuth, withAuthBody } from "../Util/superAdminRequest";

const BASE = `${VM_DOMAIN}v1/superadmin`;

export { getSuperAdminId, withAuth, withAuthBody };

// --- Dashboard ---
export const getDashboard = (filters) =>
  axios.get(`${BASE}/dashboard`, { params: withAuth(filters) });

// --- Registrations (Trial / Free / Premium) ---
export const getRegistrationStats = (filters) =>
  axios.get(`${BASE}/registrations/stats`, { params: withAuth(filters) });

// --- Venues ---
export const getVenueStats = (filters) =>
  axios.get(`${BASE}/venues/stats`, { params: withAuth(filters) });

export const getVenues = (filters) =>
  axios.get(`${BASE}/venues`, { params: withAuth(filters) });

export const getVenueById = (venueId) =>
  axios.get(`${BASE}/venues/${venueId}`, { params: withAuth() });

export const getVenueBilling = (venueId, filters = {}) =>
  axios.get(`${BASE}/venues/${venueId}/billing`, {
    params: withAuth(filters),
  });

// --- Users (venue manager accounts) ---
export const getUserStats = (filters) =>
  axios.get(`${BASE}/users/stats`, { params: withAuth(filters) });

export const getUsers = (filters) =>
  axios.get(`${BASE}/users`, { params: withAuth(filters) });

export const getUserById = (userId) =>
  axios.get(`${BASE}/users/${userId}`, { params: withAuth() });

// --- Enquiries ---
export const getEnquiryStats = (filters) =>
  axios.get(`${BASE}/enquiries/stats`, { params: withAuth(filters) });

export const getEnquiries = (filters) =>
  axios.get(`${BASE}/enquiries`, { params: withAuth(filters) });

export const getEnquiryById = (enquiry_id) =>
  axios.get(`${BASE}/enquiries/${enquiry_id}`, { params: withAuth() });

export const updateEnquiryStatus = (body) =>
  axios.patch(`${BASE}/enquiries/status`, withAuthBody(body));

// --- Events ---
export const getEvents = (filters) =>
  axios.get(`${BASE}/events`, { params: withAuth(filters) });

export const getEventTypes = (filters) =>
  axios.get(`${BASE}/event-types`, { params: withAuth(filters) });

// --- Visitor logs ---
export const getVisitorLogStats = (filters) =>
  axios.get(`${BASE}/visitor-logs/stats`, { params: withAuth(filters) });

export const getVisitorLogs = (filters) =>
  axios.get(`${BASE}/visitor-logs`, { params: withAuth(filters) });

// --- Event photos ---
export const getEventPhotos = (filters) =>
  axios.get(`${BASE}/event-photos`, { params: withAuth(filters) });

export const getEventPhotosByEnquiry = (enquiry_id, venueUserId) =>
  axios.get(`${BASE}/event-photos/${enquiry_id}`, {
    params: withAuth(venueUserId ? { user_id: venueUserId } : {}),
  });

// --- Vendors ---
export const getVendorStats = (filters) =>
  axios.get(`${BASE}/vendors/stats`, { params: withAuth(filters) });

export const getVendors = (filters) =>
  axios.get(`${BASE}/vendors`, { params: withAuth(filters) });

export const getVendorById = (id) =>
  axios.get(`${BASE}/vendors/${id}`, { params: withAuth() });

// --- Subscriptions (SuperAdmin only) ---
export const getSubscriptions = (filters) =>
  axios.get(`${BASE}/subscriptions`, { params: withAuth(filters) });

// --- Admins (SuperAdmin only) ---
export const registerAdmin = (body) =>
  axios.post(`${BASE}/admins/register`, withAuthBody(body));

export const getAdmins = (filters) =>
  axios.get(`${BASE}/admins`, { params: withAuth(filters) });

export const updateAdmin = (user_id, body) =>
  axios.put(`${BASE}/admins/${user_id}`, withAuthBody(body));

export const deleteAdmin = (user_id) =>
  axios.delete(`${BASE}/admins/${user_id}`, { params: withAuth() });

// --- Cascade filters (Country → State → City → Venue) ---
export const getFilterVenues = (filters) =>
  axios.get(`${BASE}/filters/venues`, { params: withAuth(filters) });

// --- Country master ---
export const getCountriesMaster = (filters) =>
  axios.get(`${BASE}/countries`, { params: withAuth(filters) });

export const getCountryMasterById = (cid) =>
  axios.get(`${BASE}/countries/${cid}`, { params: withAuth() });

export const addCountryMaster = (body) =>
  axios.post(`${BASE}/countries`, withAuthBody(body));

export const updateCountryMaster = (cid, body) =>
  axios.put(`${BASE}/countries/${cid}`, withAuthBody(body));

export const deleteCountryMaster = (cid) =>
  axios.delete(`${BASE}/countries/${cid}`, { params: withAuth() });

export function buildCountryListParams({ page, pageSize, search }) {
  const params = { page, no_of_rows: pageSize };
  if (search?.trim()) params.search = search.trim();
  return params;
}

/** Active countries for dropdowns (State/City masters). */
export const getCountriesForDropdown = () =>
  getCountriesMaster({ page: 0, no_of_rows: 500 });

// --- State master ---
export const getStatesMaster = (filters) =>
  axios.get(`${BASE}/states`, { params: withAuth(filters) });

export const getStateMasterById = (id) =>
  axios.get(`${BASE}/states/${id}`, { params: withAuth() });

export const addStateMaster = (body) =>
  axios.post(`${BASE}/states`, withAuthBody(body));

export const updateStateMaster = (id, body) =>
  axios.put(`${BASE}/states/${id}`, withAuthBody(body));

export const deleteStateMaster = (id) =>
  axios.delete(`${BASE}/states/${id}`, { params: withAuth() });

export function buildStateListParams({ page, pageSize, countryId, search }) {
  const params = { page, no_of_rows: pageSize };
  if (countryId && countryId !== "all") params.country_id = String(countryId);
  if (search?.trim()) params.search = search.trim();
  return params;
}

// --- City master ---
export const getCities = (filters) =>
  axios.get(`${BASE}/cities`, { params: withAuth(filters) });

export const getCityMasterById = (id) =>
  axios.get(`${BASE}/cities/${id}`, { params: withAuth() });

export const getCityMasterCountries = () =>
  axios.get(`${BASE}/cities/countries`, { params: withAuth() });

export const getCityMasterStates = (countryId) =>
  axios.get(`${BASE}/cities/states`, { params: withAuth({ country_id: countryId }) });

export const addCityMasterCity = (body) =>
  axios.post(`${BASE}/cities`, withAuthBody(body));

export const updateCityMasterCity = (id, body) =>
  axios.put(`${BASE}/cities/${id}`, withAuthBody(body));

export const deleteCityMasterCity = (id) =>
  axios.delete(`${BASE}/cities/${id}`, { params: withAuth() });

export const previewCityMasterCsv = (file, columnMapping = null) => {
  const formData = new FormData();
  formData.append("file", file);
  const id = getSuperAdminId();
  if (id) {
    formData.append("user_id", id);
    formData.append("super_admin_id", id);
  }
  if (columnMapping && Object.values(columnMapping).some(Boolean)) {
    formData.append("column_mapping", JSON.stringify(columnMapping));
  }
  return axios.post(`${BASE}/cities/csv/preview`, formData);
};

export const importCityMasterCsv = (file, columnMapping = null) => {
  const formData = new FormData();
  formData.append("file", file);
  const id = getSuperAdminId();
  if (id) {
    formData.append("user_id", id);
    formData.append("super_admin_id", id);
  }
  if (columnMapping && Object.values(columnMapping).some(Boolean)) {
    formData.append("column_mapping", JSON.stringify(columnMapping));
  }
  return axios.post(`${BASE}/cities/csv/import`, formData);
};

/** Build list query for City Master grid (0-based page, cid/state ids). */
export function buildCityListParams({ page, pageSize, countryId, stateId, citySearch }) {
  const params = { page, no_of_rows: pageSize };
  if (countryId && countryId !== "all") params.country = String(countryId);
  if (stateId && stateId !== "all") params.state = String(stateId);
  if (citySearch?.trim()) params.search = citySearch.trim();
  return params;
}

// --- Deleted accounts ---
export const getDeletedAccountsStats = (filters) =>
  axios.get(`${BASE}/deleted-accounts/stats`, { params: withAuth(filters) });

export const getDeletedAccounts = (filters) =>
  axios.get(`${BASE}/deleted-accounts`, { params: withAuth(filters) });

export const updateDeletedAccountStatus = (accountUserId, action) =>
  axios.patch(
    `${BASE}/deleted-accounts/status`,
    withAuthBody({
      account_user_id: accountUserId,
      action,
    })
  );

// --- Subscription plans (Settings) ---
export const getSubscriptionPlans = () =>
  axios.get(`${BASE}/subscription-plans`, { params: withAuth() });

export const getSubscriptionPlanById = (planId) =>
  axios.get(`${BASE}/subscription-plans/${planId}`, { params: withAuth() });

export const updateSubscriptionPlan = (planId, body) =>
  axios.put(`${BASE}/subscription-plans/${planId}`, withAuthBody(body));

// --- Error logs (Settings) ---
export const getErrorLogStats = (filters) =>
  axios.get(`${BASE}/error-logs/stats`, { params: withAuth(filters) });

export const getErrorLogs = (filters) =>
  axios.get(`${BASE}/error-logs`, { params: withAuth(filters) });

export const getErrorLogById = (errorId) =>
  axios.get(`${BASE}/error-logs/${errorId}`, { params: withAuth() });

export async function downloadExport(path, filters, filename) {
  const res = await axios.get(`${BASE}${path}`, {
    params: withAuth(filters),
    responseType: "blob",
  });
  saveAs(res.data, filename || "export.xlsx");
}

export const exportVenues = (filters) => downloadExport("/venues/export", filters, "venues.xlsx");
export const exportEnquiries = (filters) => downloadExport("/enquiries/export", filters, "enquiries.xlsx");
export const exportVendors = (filters) => downloadExport("/vendors/export", filters, "vendors.xlsx");
export const exportEvents = (filters) => downloadExport("/events/export", filters, "events.xlsx");
export const exportVisitorLogs = (filters) => downloadExport("/visitor-logs/export", filters, "visitor-logs.xlsx");

/**
 * Location filters — subAdmin: backend forces admin_scope (names); do not override.
 * SuperAdmin: pass country/state/city from cascading selects (ID or resolved label).
 */
export function locationFilterParams(loc, adminScope = null, { locked = false } = {}) {
  if (locked && adminScope) {
    const params = {};
    if (adminScope.country) params.country = String(adminScope.country);
    if (adminScope.state) params.state = String(adminScope.state);
    if (adminScope.city) params.city = String(adminScope.city);
    return params;
  }

  const params = {};
  const country = loc.countryId || loc.countryLabel;
  const state = loc.stateId || loc.stateLabel;
  const city = loc.cityId || loc.cityLabel;
  if (country) params.country = String(country);
  if (state) params.state = String(state);
  if (city) params.city = String(city);
  if (loc.venueId) params.venue_id = String(loc.venueId);
  return params;
}

/** Format dayjs date to YYYY-MM-DD for API `from` / `to`. */
export function formatApiDate(dayjsValue) {
  return dayjsValue?.isValid?.() ? dayjsValue.format("YYYY-MM-DD") : undefined;
}

/** Display API datetime (e.g. created_at) as DD-MMM-YYYY hh:mm A. */
export function formatDisplayDateTime(value) {
  if (value == null || value === "") return "—";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD-MMM-YYYY hh:mm A") : "—";
}

/** Normalize enquiry stats keys from API (lead / Lead / not_interested). */
export function normalizeEnquiryStats(data = {}) {
  const byStatus = data.by_status || {};
  return {
    total: data.total ?? 0,
    lead: data.lead ?? data.Lead ?? byStatus.lead ?? 0,
    confirmed: data.confirmed ?? data.Confirmed ?? byStatus.confirmed ?? 0,
    notInterested:
      data.not_interested ??
      data["Not-interested"] ??
      data.notInterested ??
      byStatus["not-interested"] ??
      byStatus.not_interested ??
      0,
  };
}
